import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Token refresh queue for concurrent 401 handling ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const forceLogout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

// Handle 401 errors with automatic token refresh attempt
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 (Unauthorized)
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Don't retry auth endpoints (login, refresh) to avoid infinite loops
    const url = originalRequest.url || '';
    if (url.includes('/auth/login') || url.includes('/auth/refresh') || originalRequest._retry) {
      forceLogout();
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers['Authorization'] = 'Bearer ' + token;
        return api(originalRequest);
      }).catch(err => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const res = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const newToken = res.data.access_token;
      localStorage.setItem('token', newToken);
      processQueue(null, newToken);
      originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      forceLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const getMe = () => api.get('/auth/me');
export const refreshToken = () => api.post('/auth/refresh');
export const register = (userData) => api.post('/auth/register', userData);
export const setupAdmin = () => api.post('/setup/admin');
export const requestPasswordReset = (email) => api.post('/auth/forgot-password', { email });
export const verifyResetToken = (token) => api.get(`/auth/verify-reset-token/${token}`);
export const resetPassword = (token, new_password) => api.post('/auth/reset-password', { token, new_password });

// Users
export const getUsers = (role, includeInactive = false) => api.get('/users', { params: { role, include_inactive: includeInactive } });
export const getAllUsersForDisplay = () => api.get('/users/all/for-display');
export const getUsersAvailableForEntry = () => api.get('/users/available-for-entry');
export const getUser = (id) => api.get(`/users/${id}`);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const reactivateUser = (id) => api.post(`/users/${id}/reactivate`);

// Custom Fields
export const getCustomFields = (entityType) => api.get('/custom-fields', { params: { entity_type: entityType } });
export const createCustomField = (data) => api.post('/custom-fields', data);
export const updateCustomField = (id, data) => api.put(`/custom-fields/${id}`, data);
export const deleteCustomField = (id) => api.delete(`/custom-fields/${id}`);

// Clients
export const getClients = () => api.get('/clients');
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Projects
export const getProjects = () => api.get('/projects');
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);

// Project Management (Vista Avanzata)
export const getProjectManagementData = () => api.get('/project-management');
export const updateProjectStatus = (projectId, status) => api.put(`/projects/${projectId}/status`, null, { params: { status } });

// Contracts
export const getContracts = (params) => api.get('/contracts', { params });
export const getExpiringContracts = (days = 90, status = null, extraFilters = {}) => api.get('/contracts/expiring', { params: { days, status, ...extraFilters } });
export const getContract = (id) => api.get(`/contracts/${id}`);
export const createContract = (data) => api.post('/contracts', data);
export const updateContract = (id, data) => api.put(`/contracts/${id}`, data);
export const deleteContract = (id) => api.delete(`/contracts/${id}`);

// Project Contracts (associazione multipla)
export const getProjectContracts = (projectId, includeInactive = false) => 
  api.get(`/projects/${projectId}/contracts`, { params: { include_inactive: includeInactive } });
export const getBulkProjectContracts = (projectIds, includeInactive = false) =>
  api.post('/projects/contracts/bulk', { project_ids: projectIds, include_inactive: includeInactive });
export const addContractToProject = (projectId, contractId, selectedProductIds = [], force = false) => 
  api.post(`/projects/${projectId}/contracts`, { contract_id: contractId, selected_product_ids: selectedProductIds, force });
export const removeContractFromProject = (projectId, contractId) => 
  api.delete(`/projects/${projectId}/contracts/${contractId}`);
export const updateProjectContractProducts = (projectId, contractId, selectedProductIds) =>
  api.put(`/projects/${projectId}/contracts/${contractId}/products`, { selected_product_ids: selectedProductIds });
export const getProjectActiveContracts = (projectId, referenceDate = null) => 
  api.get(`/projects/${projectId}/active-contracts`, { params: { reference_date: referenceDate } });

// Sales
export const getSales = (params) => api.get('/sales', { params });
export const createSale = (data) => api.post('/sales', data);
export const updateSale = (id, data) => api.put(`/sales/${id}`, data);
export const deleteSale = (id) => api.delete(`/sales/${id}`);
export const bulkUpdateSalesSnapshot = (data) => api.put('/sales/bulk-snapshot', data);

// Activity Types
export const getActivityTypes = () => api.get('/activity-types');
export const createActivityType = (data) => api.post('/activity-types', data);
export const updateActivityType = (id, data) => api.put(`/activity-types/${id}`, data);
export const deleteActivityType = (id) => api.delete(`/activity-types/${id}`);
export const setupActivityTypes = () => api.post('/setup/activity-types');

// Business Units
export const getBusinessUnits = (includeInactive = false) => api.get('/business-units', { params: { include_inactive: includeInactive } });
export const createBusinessUnit = (data) => api.post('/business-units', data);
export const updateBusinessUnit = (id, data) => api.put(`/business-units/${id}`, data);
export const deleteBusinessUnit = (id) => api.delete(`/business-units/${id}`);

// Products
export const getProducts = (includeInactive = false, businessUnitId = null) => 
  api.get('/products', { params: { include_inactive: includeInactive, business_unit_id: businessUnitId } });
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);

// Product Compensations
export const getProductCompensations = (productId) => api.get(`/products/${productId}/compensations`);
export const createProductCompensation = (productId, data) => api.post(`/products/${productId}/compensations`, data);
export const updateProductCompensation = (productId, compId, data) => api.put(`/products/${productId}/compensations/${compId}`, data);
export const deleteProductCompensation = (productId, compId) => api.delete(`/products/${productId}/compensations/${compId}`);

// Compensation Types
export const getCompensationTypes = (includeInactive = false) => api.get('/compensation-types', { params: { include_inactive: includeInactive } });
export const createCompensationType = (data) => api.post('/compensation-types', data);
export const updateCompensationType = (id, data) => api.put(`/compensation-types/${id}`, data);
export const deleteCompensationType = (id) => api.delete(`/compensation-types/${id}`);

// Compensation Entries
export const getCompensationEntries = (params = {}) => api.get('/compensations', { params });
export const getCompensationSummary = (params = {}) => api.get('/compensations/summary', { params });
export const createCompensationEntry = (data) => api.post('/compensations', data);
export const createCompensationEntryAdmin = (data) => api.post('/compensations/admin', data);
export const updateCompensationStatus = (id, data) => api.put(`/compensations/${id}/status`, data);
export const editCompensationEntry = (id, data) => api.put(`/compensations/${id}/edit`, data);
export const bulkEditCompensations = (data) => api.put('/compensations/bulk-edit', data);
export const bulkUpdateCompensationStatus = (data) => api.put('/compensations/bulk-status', data);
export const changeInstallments = (data) => api.post('/compensations/change-installments', data);
export const createRectification = (data) => api.post('/compensations/rectification', data);
export const deleteCompensationEntry = (id) => api.delete(`/compensations/${id}`);
export const deleteCompensationPlan = (data) => api.post('/compensations/delete-plan', data);
export const bulkDeleteCompensations = (data) => api.post('/compensations/bulk-delete', data);
export const generateProjectCompensations = (projectId, data = {}) => api.post(`/compensations/generate/${projectId}`, data);

// Project Compensation Config
export const getProjectCompensationConfig = (projectId) => api.get(`/projects/${projectId}/compensation-config`);
export const updateProjectCompensationConfig = (projectId, data) => api.put(`/projects/${projectId}/compensation-config`, data);
export const getProjectProducts = (projectId) => api.get(`/projects/${projectId}/products`);
export const getProjectFunctions = () => api.get('/project-functions');

// Contract Products
export const getContractProducts = (contractId) => api.get(`/contracts/${contractId}/products`);
export const getContractProductsBulk = (contractIds) => api.get('/contract-products/bulk', { params: { contract_ids: (contractIds || []).join(',') } });
export const addContractProduct = (contractId, data) => api.post(`/contracts/${contractId}/products`, data);
export const updateContractProduct = (contractId, contractProductId, data) => api.put(`/contracts/${contractId}/products/${contractProductId}`, data);
export const removeContractProduct = (contractId, contractProductId) => api.delete(`/contracts/${contractId}/products/${contractProductId}`);
export const updatePMSnapshot = (contractId, contractProductId, pmIds) => api.put(`/contracts/${contractId}/products/${contractProductId}/pm-snapshot`, { pm_ids: pmIds });
export const getContractStatus = (contractId) => api.get(`/contracts/${contractId}/status`);
export const getExpiringProducts = (filters = {}) => api.get('/contracts/expiring', { params: filters });
export const getExpiredProducts = (filters = {}) => api.get('/contracts/expired', { params: filters });

// Contract Attachments
export const getContractAttachments = (contractId) => api.get(`/contracts/${contractId}/attachments`);
export const uploadContractAttachment = (contractId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/contracts/${contractId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const downloadContractAttachment = (contractId, attachmentId) => 
  api.get(`/contracts/${contractId}/attachments/${attachmentId}/download`, { responseType: 'blob' });
export const deleteContractAttachment = (contractId, attachmentId) => 
  api.delete(`/contracts/${contractId}/attachments/${attachmentId}`);

// Other Commissions
export const getOtherCommissions = (params) => api.get('/other-commissions', { params });
export const createOtherCommission = (data) => api.post('/other-commissions', data);
export const updateOtherCommission = (id, data) => api.put(`/other-commissions/${id}`, data);
export const deleteOtherCommission = (id) => api.delete(`/other-commissions/${id}`);
export const bulkUpdateCommissionsSnapshot = (data) => api.put('/other-commissions/bulk-snapshot', data);

// Dashboard
export const getDashboardStats = (filters = {}) => api.get('/dashboard/stats', { params: filters });
export const getSalesByVenditore = (filters = {}) => api.get('/dashboard/sales-by-venditore', { params: filters });
export const getSalesByProject = (filters = {}) => api.get('/dashboard/sales-by-project', { params: filters });
export const getCommissionsByType = (filters = {}) => api.get('/dashboard/commissions-by-type', { params: filters });
export const getMonthlySales = (filters = {}) => api.get('/dashboard/monthly-sales', { params: filters });
export const getMonthlySuccessFees = (filters = {}) => api.get('/dashboard/monthly-success-fees', { params: filters });
export const getDashboardRankings = (filters = {}) => api.get('/dashboard/rankings', { params: filters });
export const getProductBreakdown = (filters = {}) => api.get('/dashboard/product-breakdown', { params: filters });
export const getPmBreakdown = (filters = {}) => api.get('/dashboard/pm-breakdown', { params: filters });
export const getPivotData = (filters = {}) => api.get('/dashboard/pivot-data', { params: filters });
export const getDashboardAlerts = (filters = {}) => api.get('/dashboard/alerts', { params: filters });
export const getProjectStatuses = () => api.get('/projects/statuses');

// Renewals Analytics
export const getRenewalAnalytics = (filters = {}) => api.get('/analytics/renewals', { params: filters });

// System Settings
export const getStorageStats = () => api.get('/system-settings/storage-stats');
export const getSystemSettings = () => api.get('/system-settings');
export const updateSystemSettings = (data) => api.put('/system-settings', data);

// Email
export const getEmailStatus = () => api.get('/email/status');
export const sendTestEmail = (toEmail) => api.post('/email/test', null, { params: { to_email: toEmail } });

// Notifications
export const getNotifications = (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } });
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');

// Project Users (Assegnazioni con storico)
export const getProjectUsers = (projectId, includeInactive = false) => 
  api.get(`/project-users/${projectId}`, { params: { include_inactive: includeInactive } });
export const assignUserToProject = (data) => api.post('/project-users', data);
export const endProjectAssignment = (assignmentId) => api.put(`/project-users/${assignmentId}/end`);

// Client Account History
export const getClientAccountHistory = (clientId, includeInactive = true) => 
  api.get(`/client-account-history/${clientId}`, { params: { include_inactive: includeInactive } });
export const assignAccountToClient = (clientId, accountUserId) => 
  api.post('/client-account-history', { client_id: clientId, account_user_id: accountUserId });

// Entity Change Log
export const getEntityChangeLog = (entityType, entityId) => 
  api.get(`/entity-change-log/${entityType}/${entityId}`);

// Commission Versions (Audit Trail)
export const getCommissionVersions = (commissionType, commissionId) => 
  api.get(`/commission-versions/${commissionType}/${commissionId}`);

// Planners (Toolkit)
export const getPlanners = () => api.get('/planners');
export const getPlanner = (id) => api.get(`/planners/${id}`);
export const createPlanner = (data) => api.post('/planners', data);
export const updatePlanner = (id, data) => api.put(`/planners/${id}`, data);
export const deletePlanner = (id) => api.delete(`/planners/${id}`);
export const duplicatePlanner = (id) => api.post(`/planners/${id}/duplicate`);

// Project Functions enum
export const PROJECT_FUNCTIONS = [
  { value: 'sales_coach', label: 'Sales Coach Junior' },
  { value: 'sales_coach_senior', label: 'Sales Coach Senior' },
  { value: 'marketing_manager', label: 'Marketing Manager Junior' },
  { value: 'marketing_manager_senior', label: 'Marketing Manager Senior' },
  { value: 'hunter_manager', label: 'Hunter Manager' },
  { value: 'venditore', label: 'Sales' },
  { value: 'setter', label: 'Setter' },
  { value: 'hunter', label: 'Hunter' },
  { value: 'hunter_senior', label: 'Hunter Senior' },
  { value: 'copywriter', label: 'Copywriter' },
  { value: 'media_buyer', label: 'Media Buyer' },
  { value: 'supervisor_sales', label: 'Supervisor Sales' },
  { value: 'supervisor_sales_hunter', label: 'Supervisor Sales Hunter' },
  { value: 'supervisor_marketing', label: 'Supervisor Marketing' },
];

// Customer Feedback
export const getClientActiveBusinessUnits = (clientId) => api.get(`/clients/${clientId}/active-business-units`);
export const getCustomerFeedbacks = (params = {}) => api.get('/customer-feedbacks', { params });
export const getCustomerFeedbackDashboard = (params = {}) => api.get('/customer-feedbacks/dashboard', { params });
export const getCustomerFeedback = (id) => api.get(`/customer-feedbacks/${id}`);
export const createCustomerFeedback = (data) => api.post('/customer-feedbacks', data);
export const updateCustomerFeedback = (id, data) => api.put(`/customer-feedbacks/${id}`, data);
export const deleteCustomerFeedback = (id) => api.delete(`/customer-feedbacks/${id}`);

// ─── Front-End Pipeline ───
export const listFrontendPipeline = (params = {}) => api.get('/frontend-pipeline', { params });
export const getFrontendPipelineCounters = () => api.get('/frontend-pipeline/counters');
export const getFrontendPipelineEntry = (id, includeAllClients = false) => api.get(`/frontend-pipeline/${id}`, { params: includeAllClients ? { include_all_clients: true } : {} });
export const updateFrontendPipelineEntry = (id, data) => api.put(`/frontend-pipeline/${id}`, data);
export const winFrontendPipelineEntry = (id, data) => api.post(`/frontend-pipeline/${id}/win`, data);
export const loseFrontendPipelineEntry = (id, data) => api.post(`/frontend-pipeline/${id}/lose`, data);
export const deleteFrontendPipelineEntry = (id) => api.delete(`/frontend-pipeline/${id}`);
export const reopenFrontendPipelineEntry = (id, newStage = null) => api.post(`/frontend-pipeline/${id}/reopen`, newStage ? { new_stage: newStage } : {});
export const backfillFrontendPipeline = () => api.post('/frontend-pipeline/backfill');
export const getFrontendPipelineProducts = () => api.get('/frontend-pipeline/products');
export const getFrontendAnalytics = (filters = {}) => api.get('/analytics/frontend', { params: filters });
export const checkClientName = (name, excludeId = null) => api.get('/clients/check-name', { params: { name, ...(excludeId ? { exclude_id: excludeId } : {}) } });

// ─── Sales Roster (admin only) ───
export const getSalesRoster = (params = {}) => api.get('/sales-roster', { params });
export const hideSalesRosterUser = (userId) => api.post(`/sales-roster/hidden/${userId}`);
export const unhideSalesRosterUser = (userId) => api.delete(`/sales-roster/hidden/${userId}`);
export const updateSalesRosterMeta = (userId, patch) => api.put(`/sales-roster/meta/${userId}`, patch);

// Contracts Export CSV
export const getContractsExportFields = () => api.get('/contracts/export/fields');
export const exportContractsCsv = (body) => api.post('/contracts/export/csv', body, { responseType: 'blob' });

// System Roles enum
export const SYSTEM_ROLES = [
  { value: 'pm', label: 'Project Manager' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'sales', label: 'Operativo' },
];

// ─── Fiscal Credits (admin only) ───
export const getFiscalCreditsSummary = () => api.get('/fiscal-credits/summary');
export const getFiscalCreditPurchases = () => api.get('/fiscal-credits/purchases');
export const getFiscalCreditPurchase = (id) => api.get(`/fiscal-credits/purchases/${id}`);
export const createFiscalCreditPurchase = (data) => api.post('/fiscal-credits/purchases', data);
export const updateFiscalCreditPurchase = (id, data) => api.put(`/fiscal-credits/purchases/${id}`, data);
export const deleteFiscalCreditPurchase = (id) => api.delete(`/fiscal-credits/purchases/${id}`);
export const getFiscalCreditUsages = () => api.get('/fiscal-credits/usages');
export const createFiscalCreditUsage = (data) => api.post('/fiscal-credits/usages', data);
export const updateFiscalCreditUsage = (id, data) => api.put(`/fiscal-credits/usages/${id}`, data);
export const deleteFiscalCreditUsage = (id) => api.delete(`/fiscal-credits/usages/${id}`);
export const getFiscalCreditMovements = () => api.get('/fiscal-credits/movements');

export default api;
